import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import { IconAlert, IconCheck, IconLoader } from "../components/Icons";

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  interface ApiError {
    response?: { data?: { detail?: string } };
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password.length < 8) { setErr(t("reset.tooShort")); return; }
    if (password !== password2) { setErr(t("reset.mismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (e: unknown) {
      const ae = e as ApiError;
      setErr(ae.response?.data?.detail ?? t("reset.error"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--bg)", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div className="card" style={{ width: "100%", maxWidth: 400, padding: 32 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>{t("reset.title")}</h1>
        <p style={{ color: "var(--text-secondary)", marginBottom: 24 }}>{t("reset.subtitle")}</p>

        {!token ? (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: "var(--radius-sm)",
              background: "var(--red-dim)",
              color: "var(--red)",
              fontSize: 13,
            }}
          >
            {t("reset.invalid")}
          </div>
        ) : done ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 14px",
              borderRadius: "var(--radius-sm)",
              background: "var(--green-dim)",
              color: "var(--green)",
              fontSize: 13,
            }}
          >
            <IconCheck size={16} />
            {t("reset.done")}
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label>{t("reset.newPassword")}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={8}
              />
            </div>
            <div>
              <label>{t("reset.newPassword2")}</label>
              <input
                type="password"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                placeholder="••••••••"
                required
                minLength={8}
              />
            </div>

            {err && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 14px",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--red-dim)",
                  color: "var(--red)",
                  fontSize: 13,
                }}
              >
                <IconAlert size={16} />
                {err}
              </div>
            )}

            <button type="submit" className="primary" disabled={loading}>
              {loading ? <IconLoader size={18} className="spin" /> : t("reset.submit")}
            </button>
          </form>
        )}

        <p style={{ marginTop: 24, textAlign: "center", color: "var(--text-secondary)", fontSize: 14 }}>
          <Link to="/login" style={{ color: "var(--accent)", fontWeight: 600 }}>{t("reset.backToLogin")}</Link>
        </p>
      </div>
    </div>
  );
}
